import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { i18nObject } from 'boot/i18n.js'
import { useHelpers } from 'common/composition/useHelpers.js'
import { useVfmModals } from 'hooks/modals.js'
import { useSystemStore } from 'stores/system'
import { getWelcomeOfferAuthPlatform, useWelcomeOffer } from 'src/common/composition/useWelcomeOffer'
import { BASE_URL } from 'src/common/config.js'
import { Cookies } from 'src/core/utils/cookies'
import { getConfig } from '@/configs/index.js'

export function useAuth() {
  const { altW } = storeToRefs(useSystemStore())
  const config = getConfig()
  const { authModal } = useVfmModals()
  const { isSelectionModalOpen, selectedOffer, showPostRegModal, showBanner } = useWelcomeOffer()
  const $helpers = useHelpers()
  let t, locale
  try {
    const i18n = useI18n()
    t = i18n.t
    locale = i18n.locale
  } catch {
    t = (key) => i18nObject?.global?.t(key) ?? key
    locale = i18nObject?.global?.locale ?? { value: 'ru' }
  }

  function openModal(tab, props = {}) {
    authModal({ tab, ...props }).open()
  }

  const authByDeepLink = config.authDeepLink

  const authLinkDomain = process.env.AUTH_LINK

  function authRedirect(options = {}) {
    $helpers.loading.show({
      message: t('authorization') + '...',
    })

    const advertInfo = getAdvertInfoFromRefData()

    location.href = buildUrl({
      domain: authLinkDomain,
      path: '/oauth2/authorization/betweb-api-oidc',
      params: {
        locale: locale.value,
        'redirect-url': getExternalAuthRedirectUrl(),
        'advert-info': advertInfo,
        platform: getWelcomeOfferAuthPlatform(altW.value ? selectedOffer.value : null),
        source: 'gambling',
        prompt: options?.prompt ?? null,
      },
    })
  }

  function login(props = {}) {
    if (altW.value) {
      showPostRegModal.value = true
      showBanner.value = false
    }

    if (typeof authByDeepLink === 'function' && !props?.testAuthByLink) {
      return authByDeepLink()
    }
    if (authLinkDomain) {
      return authRedirect({ prompt: 'login' })
    }
    openModal('login', props)
  }

  function register(props = {}) {
    if (shouldShowWelcomeOfferModal(props)) {
      isSelectionModalOpen.value = true
      return
    }

    if (typeof authByDeepLink === 'function') {
      return authByDeepLink()
    }
    if (authLinkDomain) {
      return authRedirect({ prompt: 'create' })
    }
    openModal('reg', props)
  }

  function auth() {
    const tab = localStorage.getItem('hasAccount') ? 'login' : 'reg'
    if (tab === 'login') {
      login()
    } else {
      register()
    }
  }

  function shouldShowWelcomeOfferModal(props) {
    return altW.value && !props?.skipWelcomeOffer
  }

  return {
    login,
    register,
    auth,
  }
}

export function getExternalAuthRedirectUrl() {
  const linkAfterAuth = sessionStorage.getItem('linkAfterAuth') ?? ''

  return `${location.origin}${BASE_URL}${linkAfterAuth}`
}

function buildUrl({ domain, path = '', params = {} }) {
  const url = new URL(path, domain)

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.append(key, value)
    }
  })

  return url.toString()
}

function getAdvertInfoFromRefData() {
  const refData = Cookies.get('ref_data')
  const parsedRefData = parseRefData(refData)

  if (!parsedRefData || Object.keys(parsedRefData).length === 0) {
    return undefined
  }

  return new URLSearchParams(parsedRefData).toString()
}

function parseRefData(refData) {
  if (!refData) {
    return null
  }

  if (typeof refData === 'object') {
    return refData
  }

  if (typeof refData !== 'string') {
    return null
  }

  try {
    const parsed = JSON.parse(refData)
    return typeof parsed === 'object' && parsed !== null ? parsed : null
  } catch {
    return null
  }
}
